import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    Event as RouterEvent,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
    Router,
} from '@angular/router';

/**
 * Thin top-of-viewport progress bar that animates while the Router is
 * navigating between routes. It gives the user instant feedback when a
 * lazy chunk is downloading or a guard is resolving, even before the
 * target page mounts (and triggers its own `<app-page-loader />`).
 *
 * - Shows after a small grace period so fast/cached navigations don't flash.
 * - Hides on `NavigationEnd`, `NavigationCancel`, and `NavigationError`.
 */
@Component({
    selector: 'app-route-progress',
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div
            class="route-progress"
            [class.route-progress--active]="visible()"
            role="progressbar"
            aria-hidden="true"
        >
            <div class="route-progress__bar"></div>
        </div>
    `,
    styles: [
        `
            :host {
                display: contents;
            }
            .route-progress {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                height: 3px;
                z-index: 2000;
                pointer-events: none;
                opacity: 0;
                transition: opacity 160ms ease-out;
            }
            .route-progress--active {
                opacity: 1;
            }
            .route-progress__bar {
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                width: 30%;
                background: var(--color-accent, #e11d48);
                box-shadow: 0 0 8px rgba(225, 29, 72, 0.5);
                border-radius: 0 2px 2px 0;
                animation: route-progress-slide 1.1s ease-in-out infinite;
            }
            @keyframes route-progress-slide {
                0% {
                    left: -30%;
                    width: 30%;
                }
                50% {
                    left: 30%;
                    width: 40%;
                }
                100% {
                    left: 100%;
                    width: 30%;
                }
            }
            @media (prefers-reduced-motion: reduce) {
                .route-progress__bar {
                    animation-duration: 2.4s;
                }
            }
        `,
    ],
})
export class RouteProgressComponent {
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly visible = signal(false);

    /** Delay before showing the bar so cached navigations don't flicker. */
    private static readonly SHOW_AFTER_MS = 120;

    private showTimer?: ReturnType<typeof setTimeout>;

    constructor() {
        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => this.handle(event));
    }

    private handle(event: RouterEvent): void {
        if (event instanceof NavigationStart) {
            this.clearTimer();
            this.showTimer = setTimeout(() => {
                this.visible.set(true);
            }, RouteProgressComponent.SHOW_AFTER_MS);
            return;
        }

        if (
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError
        ) {
            this.clearTimer();
            this.visible.set(false);
        }
    }

    private clearTimer(): void {
        if (this.showTimer) {
            clearTimeout(this.showTimer);
            this.showTimer = undefined;
        }
    }
}
